import UploadFileDropzone from '@/components/common/UploadFileDropzone';
import { useUploadFileMutation } from '@/features/api/endpoints/fileEndpoints';
import type { DirectusFiles } from '@/models/types';
import type { PermissionId } from '@/shared/auth/permissions';
import { useIsAdmin } from '@/shared/auth/useDirectusUser';
import { useUserHasSomePermissions } from '@/shared/auth/useUserAffectations';
import { useIsXs } from '@/shared/utils/mediaQuery-utils';
import {
  type AcceptedFileTypes,
  type FileValidator,
  getUploadErrorKey,
} from '@/shared/utils/upload-utils';
import { ActionIcon, Button, Tooltip, type MantineSize } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconUpload } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import type { ReactNode } from 'react';

type Props = {
  /** Identifiant du dossier Directus où le fichier est stocké */
  folder: string;
  /** Permissions donnant accès au composant en plus du rôle administrateur */
  permissionIds?: PermissionId[];
  /** Libellé du bouton (par défaut : `file.upload.title`) */
  label?: string;
  /** Types de fichier autorisés (ex. `FILE_TYPES.csv`) ; tous les types si absent */
  accept?: AcceptedFileTypes;
  /** Taille maximale du fichier, en octets ; pas de limite si absente */
  maxSize?: number;
  /** Contrôles spécifiques du contenu, exécutés avant l'envoi (ex. `validateThesaurusImportFile`) */
  validate?: FileValidator;
  /**
   * Contenu de la modale à la place de la dropzone par défaut (ex. formulaire avec des champs
   * en plus) ; `upload` envoie le fichier sous le nom donné. `accept`, `maxSize` et `validate`
   * sont alors ignorés.
   */
  renderContent?: (upload: UploadFile) => ReactNode;
  /** Largeur de la modale (`size` de Mantine, `md` par défaut) */
  modalSize?: MantineSize | number | string;
  onUploaded?: (file: DirectusFiles) => void;
};

/**
 * Envoi d'un fichier dans le dossier, sous `fileName` (nom d'origine si absent). Renvoie la clé
 * Tolgee de l'erreur, à afficher dans la modale qui reste ouverte, ou `null` en cas de succès.
 */
export type UploadFile = (file: File, fileName?: string) => Promise<string | null>;

const UploadFileToFolder = ({
  folder,
  permissionIds,
  label,
  accept,
  maxSize,
  validate,
  renderContent,
  modalSize,
  onUploaded,
}: Props) => {
  const { t } = useTranslate();
  const isXs = useIsXs();
  const isAdmin = useIsAdmin();
  const { result: hasPermission } = useUserHasSomePermissions(permissionIds ?? []);
  const [uploadFile] = useUploadFileMutation();

  if (!isAdmin && !hasPermission) {
    return null;
  }

  const buttonLabel = label ?? t('file.upload.title');

  const handleUploadFile: UploadFile = async (file, fileName = file.name) => {
    const formData = new FormData();
    formData.append('file', file, fileName);
    formData.append('folder', folder);

    try {
      const uploadedFile = await uploadFile(formData).unwrap();
      //const storedFile = await updateFile({ id: uploadedFile.id, body: { folder } }).unwrap();

      notifications.show({
        color: 'green',
        message: t('file.upload.success'),
      });

      modals.closeAll();
      onUploaded?.(uploadedFile);
      return null;
    } catch (error: unknown) {
      return getUploadErrorKey(error, 'file.upload.error');
    }
  };

  const handleUploadClick = () => {
    modals.open({
      title: buttonLabel,
      fullScreen: isXs,
      size: modalSize,
      children: renderContent ? (
        renderContent(handleUploadFile)
      ) : (
        <UploadFileDropzone
          accept={accept}
          maxSize={maxSize}
          validate={validate}
          onFileSelected={handleUploadFile}
        />
      ),
    });
  };

  return (
    <>
      <Tooltip label={buttonLabel} withArrow>
        <ActionIcon
          onClick={handleUploadClick}
          aria-label={buttonLabel}
          hiddenFrom="sm"
          variant="light"
        >
          <IconUpload size={18} />
        </ActionIcon>
      </Tooltip>
      <Button
        leftSection={<IconUpload size={18} />}
        variant="light"
        onClick={handleUploadClick}
        visibleFrom="sm"
      >
        {buttonLabel}
      </Button>
    </>
  );
};

export default UploadFileToFolder;
